import { parseInlineImages } from './inlineImages';

// How a ticket's conversation is laid out: newest message first, and every
// message long enough to need it folds down to a one-line preview (author,
// date, first few words) behind a ▸ toggle. Long back-and-forths no longer
// mean scrolling through each one in full to reach the latest (cTKT-00295).

/** Characters of message text shown on a collapsed row. */
export const PREVIEW_CHARS = 140;

interface ThreadMessage {
  id?: string;
  _id?: string;
  content?: unknown;
  createdAt?: string | null;
  attachments?: unknown[] | null;
}

export function messageId(m: ThreadMessage): string {
  return String(m.id ?? m._id ?? '');
}

/**
 * The message as one line of plain text for the collapsed row. Inline
 * screenshots drop out (they're `![name](url)` references, not words), and
 * line breaks and runs of spaces fold to single spaces.
 */
export function messagePreview(content: unknown, max = PREVIEW_CHARS): { text: string; truncated: boolean } {
  const raw = String(content ?? '');
  const words = parseInlineImages(raw)
    .map(seg => (seg.type === 'text' ? seg.text : ' '))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  if (words.length <= max) return { text: words, truncated: false };
  // Cut on a word boundary when there's one reasonably close to the limit.
  const cut = words.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return { text: `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`, truncated: true };
}

/**
 * Does this message have anything a one-line preview would hide? A short
 * plain message is shown whole either way, so it gets no toggle.
 */
export function isCollapsible(m: ThreadMessage): boolean {
  const content = String(m.content ?? '');
  if ((m.attachments?.length ?? 0) > 0) return true;
  if (parseInlineImages(content).some(seg => seg.type !== 'text')) return true;
  if (/\n/.test(content.trim())) return true;
  return messagePreview(content).truncated;
}

/**
 * Newest first. The API returns the thread oldest-first (the PDF export and
 * AI prompt read it in that order), so the flip happens here, for display
 * only. Messages with the same timestamp keep their reversed API order.
 */
export function newestFirst<T extends ThreadMessage>(messages: readonly T[]): T[] {
  return messages
    .map((m, i) => ({ m, i, t: m.createdAt ? new Date(m.createdAt).getTime() : 0 }))
    .sort((a, b) => (b.t - a.t) || (b.i - a.i))
    .map(x => x.m);
}
